export interface CalculatorInputs {
  annualWaterVolume: number;
  annualWaterVolumeUnit: 'million_gallons' | 'acre_feet';
  waterType: 'high_bicarb_well' | 'high_bicarb_surface' | 'moderate' | 'not_sure';
  sulfuricPrice: number;
  sulfuricPriceUnit: 'per_ton' | 'per_gallon';
  sulfurPricePerTon: number;
  burnerPrice?: number;
}

export interface CalculatorResults {
  acidCost: number;
  sulfurCost: number;
  annualSavings: number;
  paybackYears?: number;
  volumeMG: number;
  totalAcidTons: number;
  actualSulfurTons: number;
}

const ACID_TONS_PER_MG: Record<string, number> = {
  high_bicarb_well: 0.46,
  high_bicarb_surface: 0.46,
  moderate: 0.30,
  not_sure: 0.38,
};

const SULFUR_TO_ACID_RATIO = 3;

const BURNER_EFFICIENCY = 0.9;

const ACRE_FEET_TO_MG = 0.325851;

const SULFURIC_DENSITY_LB_PER_GAL = 15.3;
const LB_PER_TON = 2000;

export function calculateCosts(inputs: CalculatorInputs): CalculatorResults {
  let volumeMG = inputs.annualWaterVolume;
  if (inputs.annualWaterVolumeUnit === 'acre_feet') {
    volumeMG = inputs.annualWaterVolume * ACRE_FEET_TO_MG;
  }

  const acidTonsPerMG = ACID_TONS_PER_MG[inputs.waterType];
  const totalAcidTons = acidTonsPerMG * volumeMG;

  let sulfuricPricePerTon = inputs.sulfuricPrice;
  if (inputs.sulfuricPriceUnit === 'per_gallon') {
    sulfuricPricePerTon = inputs.sulfuricPrice * (LB_PER_TON / SULFURIC_DENSITY_LB_PER_GAL);
  }

  const acidCost = totalAcidTons * sulfuricPricePerTon;

  const theoreticalSulfurTons = totalAcidTons / SULFUR_TO_ACID_RATIO;
  const actualSulfurTons = theoreticalSulfurTons / BURNER_EFFICIENCY;
  const sulfurCost = actualSulfurTons * inputs.sulfurPricePerTon;

  const annualSavings = Math.max(acidCost - sulfurCost, 0);

  let paybackYears: number | undefined;
  if (inputs.burnerPrice && annualSavings > 0) {
    paybackYears = inputs.burnerPrice / annualSavings;
  }

  return {
    acidCost: Math.round(acidCost),
    sulfurCost: Math.round(sulfurCost),
    annualSavings: Math.round(annualSavings),
    paybackYears: paybackYears ? Math.round(paybackYears * 10) / 10 : undefined,
    volumeMG: Math.round(volumeMG * 100) / 100,
    totalAcidTons: Math.round(totalAcidTons * 10) / 10,
    actualSulfurTons: Math.round(actualSulfurTons * 10) / 10,
  };
}

export const WATER_TYPE_OPTIONS = [
  { value: 'high_bicarb_well', label: 'High bicarbonate well water' },
  { value: 'high_bicarb_surface', label: 'High bicarbonate surface/canal water' },
  { value: 'moderate', label: 'Moderate alkalinity' },
  { value: 'not_sure', label: 'Not sure – just estimate it for me' },
];

export const ROLE_OPTIONS = [
  { value: 'grower', label: 'Grower' },
  { value: 'irrigation_manager', label: 'Irrigation manager' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'dealer', label: 'Dealer' },
  { value: 'other', label: 'Other' },
];
